import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { Flame } from 'lucide-react';
import { PageQueryView } from '@/components/customisation/render/page-view';
import { Button } from '@/components/ui/button';
import { publicPageQuery, publicSiteQuery } from '@/lib/customisation/queries';
import { sessionQuery } from '@/lib/session';

export const Route = createFileRoute('/p/$slug')({ component: PublicPage });

/**
 * A public custom page for anyone, signed in or not, in a minimal shell: the provider's
 * name, the page, and a way into the panel. The API only serves pages whose visibility is
 * `public` here; a customers-only page answers 404 to a visitor.
 */
function PublicPage() {
  const { slug } = Route.useParams();
  const page = useQuery(publicPageQuery(slug));
  const site = useQuery(publicSiteQuery);
  const session = useQuery(sessionQuery);
  const name =
    site.data?.branding.displayName ??
    site.data?.branding.panelName ??
    session.data?.panel.name ??
    'Hearth';
  const logo = site.data?.logos.primary ?? null;
  const signedIn = session.data?.authenticated === true;
  return (
    <div className="flex min-h-dvh flex-col">
      <header className="bg-background/80 sticky top-0 z-40 border-b backdrop-blur-md">
        <div className="mx-auto flex h-14 w-full max-w-[1400px] items-center gap-3 px-4 sm:px-6 lg:px-8">
          <Link to="/" className="flex min-w-0 items-center gap-2.5 font-semibold tracking-tight">
            {logo ? (
              <img src={logo} alt="" className="h-7 w-auto" />
            ) : (
              <span className="bg-card text-brand shadow-card grid size-8 place-items-center rounded-lg border">
                <Flame className="size-4" aria-hidden />
              </span>
            )}
            <span className="truncate">{name}</span>
          </Link>
          <div className="ml-auto">
            {signedIn ? (
              <Button asChild size="sm" variant="outline">
                <Link to="/">Open the panel</Link>
              </Button>
            ) : (
              <Button asChild size="sm">
                <Link to="/login" search={{}}>
                  Sign in
                </Link>
              </Button>
            )}
          </div>
        </div>
      </header>
      <main
        id="main"
        className="mx-auto w-full max-w-[1400px] flex-1 px-4 py-6 sm:px-6 sm:py-8 lg:px-8"
      >
        <PageQueryView query={page} homeLabel={signedIn ? 'Back to the panel' : 'Go to sign in'} />
      </main>
      {site.data?.branding.footerText && (
        <footer className="text-muted-foreground border-t px-4 py-4 text-center text-xs">
          {site.data.branding.footerText}
        </footer>
      )}
    </div>
  );
}
